import type { Grammar, Graph, Rule } from 'graph-grammar'
import { Engine } from 'graph-grammar'
import { cloneGraph } from 'graph-grammar'
import { buildExample } from 'graph-grammar/examples'
import type { NodeStyleResolver } from './nodeStyle.ts'
import { mergeGrammars, type GrammarModule } from './mergeGrammars.ts'
import { defaultSettings, clampSettings, parseSettings, serializeSettings, type WorkbenchSettings } from './settings.ts'

/** localStorage key the view/layout settings persist under. */
const SETTINGS_KEY = 'graph-grammar.settings'

/** Read persisted settings (defaults if none / unavailable / corrupt). Guarded
 *  so a non-browser context — tests, SSR — falls back to defaults silently. */
function loadStoredSettings(): WorkbenchSettings {
  try {
    if (typeof localStorage === 'undefined') return defaultSettings()
    const raw = localStorage.getItem(SETTINGS_KEY)
    if (!raw) return defaultSettings()
    const res = parseSettings(raw)
    return res.ok ? res.settings : defaultSettings()
  } catch {
    return defaultSettings()
  }
}

/**
 * A tracked grammar module plus its enabled flag. A module stays in the loaded
 * set when disabled (so the user keeps it without re-importing), but its rules
 * are NOT composed into the active grammar while `enabled` is false.
 */
interface TrackedModule extends GrammarModule {
  enabled: boolean
}

/** A flat, read-only view of a tracked module for the sidebar UI. */
export interface LoadedModule {
  key: string
  title: string
  enabled: boolean
  /** True for the first ENABLED module , the one that supplies start/config. */
  primary: boolean
}

/** One completed run of a seed sweep: the seed used and the resulting graph. */
interface BatchRun {
  seed: number
  graph: Graph
  /** Rewrite steps applied before the run reached a fixpoint / its cap. */
  steps: number
}

/** A read-only summary of one batch run for the navigator UI. */
export interface BatchRunSummary {
  seed: number
  steps: number
  nodes: number
  edges: number
}

/** Options for a seed-sweep batch run (see {@link AppState.runBatch}). */
export interface BatchRunOptions {
  /** How many runs to perform. */
  count: number
  /** `random` picks fresh random seeds; `range` uses consecutive seeds. */
  mode: 'random' | 'range'
  /** First seed for `range` mode (consecutive: start, start+1, …). */
  start?: number
}

export type AppEvent =
  | 'grammar' // grammar structure changed (rules added/removed/edited)
  | 'rules' // rule list changed
  | 'selectRule' // active rule selection changed
  | 'graph' // host graph changed (engine state)
  | 'config' // run config changed
  | 'running' // play/pause state changed
  | 'recenter' // request the graph view to re-frame/centre
  | 'modules' // the set of loaded grammar modules changed (add/remove/replace)
  | 'batch' // batch-run results changed (new batch, navigation, or cleared)
  | 'settings' // view / layout preferences changed (force tuning, display toggles)

type Listener = () => void

/**
 * A serializable capture of the authored state, used for undo/redo. Covers the
 * grammar (rules + config + start graph), the current host graph (canvas edits
 * live here until pinned), and the rule selection. Transient run state (play
 * timer, step counters, last-highlight) is deliberately excluded.
 */
export interface AppSnapshot {
  grammar: Grammar
  graph: Graph
  activeRuleId: string | null
}

const bareGrammar = (): Grammar => ({
  id: 'unknown',
  name: 'Unknown',
  rules: [],
  config: {
    strategy: 'random',
    seed: 0,
    maxSteps: 100,
    maxNodes: 0,
  },
  /** Starting graph (axiom). */
  start: {
    nodes: [],
    edges: [],
  },
})

/**
 * Central, observable application state. Views subscribe to coarse-grained
 * events and re-read what they need. Keeps the no-framework UI coordinated.
 */
export class AppState {
  grammar: Grammar
  engine: Engine
  activeRuleId: string | null = null
  running = false
  speed = 6 // steps per second when playing
  /** highlight info from the last applied rewrite, for the graph view. */
  lastHighlight: { created: Set<string>; deleted: Set<string> } | null = null
  /** Optional per-node appearance override for the host graph canvas. The
   *  renderer reads this each frame; set it via `<Workbench nodeStyle={…}>`. */
  nodeStyle?: NodeStyleResolver
  /**
   * Frequently-adjusted view/layout preferences (force tuning, display toggles).
   * Distinct from the authored grammar/graph: these are workspace prefs, kept
   * OUT of undo/redo, auto-persisted to localStorage, and serialisable to a
   * portable JSON file. Mutate only through {@link setSettings}.
   */
  settings: WorkbenchSettings = loadStoredSettings()

  /**
   * The grammar MODULES currently composed into `grammar`, keyed by module key
   * (e.g. an example `key`), in load order. Insertion order matters: the first
   * module is the PRIMARY and supplies the start graph + base config (see
   * mergeGrammars). Empty when the active grammar came from a path that doesn't
   * track modules (a fresh `AppState()`, an import, an undo, …) , the UI treats
   * "no tracked modules" as "nothing is selected".
   */
  private modules = new Map<string, TrackedModule>()

  /**
   * Results of the most recent seed-sweep batch run, or null when none is
   * active. `runs` is in seed order; `index` is the run currently shown on the
   * canvas. Cleared whenever the grammar or start axiom changes (the captured
   * graphs would no longer correspond to the active grammar).
   */
  private batch: { runs: BatchRun[]; index: number } | null = null

  /** Called after a tracked, undoable mutation with a short action label. The
   *  history layer (see history.ts) wires this to record a snapshot. */
  onCommit?: (label: string) => void
  /** Guards against recording history while we're applying an undo/redo. */
  private restoring = false

  private listeners = new Map<AppEvent, Set<Listener>>()

  constructor(grammar?: Grammar) {
    this.grammar = grammar ?? bareGrammar()
    this.engine = new Engine(this.grammar)
    this.activeRuleId = this.grammar.rules[0]?.id ?? null
  }

  on(ev: AppEvent, fn: Listener): () => void {
    let set = this.listeners.get(ev)
    if (!set) this.listeners.set(ev, (set = new Set()))
    set.add(fn)
    return () => set!.delete(fn)
  }

  emit(...evs: AppEvent[]) {
    for (const ev of evs) for (const fn of this.listeners.get(ev) ?? []) fn()
  }

  // ---- view / layout settings ----

  /**
   * Patch the workbench settings (force tuning, display toggles, layout). The
   * patch is merged over the current values, clamped to valid ranges, persisted
   * to localStorage, and broadcast via the `settings` event so the renderer and
   * toolbar re-sync. `force` is shallow-merged so `{ force: { charge } }` keeps
   * the other force knobs.
   */
  setSettings(patch: Partial<WorkbenchSettings>) {
    this.settings = clampSettings({
      ...this.settings,
      ...patch,
      force: { ...this.settings.force, ...(patch.force ?? {}) },
    })
    this.persistSettings()
    this.emit('settings')
  }

  /** Restore the stock view/layout settings. */
  resetSettings() {
    this.setSettings(defaultSettings())
  }

  /** Replace settings wholesale from a serialised settings JSON document. */
  importSettings(json: string): { ok: boolean; error?: string } {
    const res = parseSettings(json)
    if (!res.ok) return res
    this.setSettings(res.settings)
    return { ok: true }
  }

  /** The current settings as a portable JSON document. */
  exportSettings(): string {
    return serializeSettings(this.settings)
  }

  /** Best-effort write-through to localStorage (no-op outside the browser). */
  private persistSettings() {
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(SETTINGS_KEY, serializeSettings(this.settings))
    } catch {
      /* storage full / unavailable — settings stay in memory */
    }
  }

  // ---- undo/redo support ----

  /** Announce a tracked, undoable change (no-op while restoring). */
  private commit(label: string) {
    if (!this.restoring) this.onCommit?.(label)
  }

  /** A structural host-graph edit from the canvas: refresh views AND record it.
   *  Distinct from `emit("graph")`, which run steps use and is NOT tracked. */
  commitGraph(label: string) {
    this.emit('graph')
    this.commit(label)
  }

  /** Capture the authored state for the history stack. */
  snapshot(): AppSnapshot {
    return {
      grammar: structuredClone(this.grammar),
      graph: cloneGraph(this.engine.graph),
      activeRuleId: this.activeRuleId,
    }
  }

  /** Restore a previously captured snapshot (undo/redo). Does not itself record. */
  restore(s: AppSnapshot) {
    this.restoring = true
    try {
      this.grammar = structuredClone(s.grammar)
      // Rebuild the engine on the restored grammar, then load the captured host
      // graph as the current state (the constructor clones it for us).
      this.engine = new Engine(this.grammar, s.graph)
      this.activeRuleId = this.grammar.rules.some((r) => r.id === s.activeRuleId)
        ? s.activeRuleId
        : (this.grammar.rules[0]?.id ?? null)
      this.running = false
      this.lastHighlight = null
      // Snapshots capture the composed grammar, not the module identities that
      // produced it, so drop the tracked module set on restore. The UI then
      // shows "nothing selected" until the user loads modules again , safer
      // than leaving a stale set that no longer matches `grammar`.
      this.modules.clear()
      this.batch = null
      this.emit('grammar', 'rules', 'selectRule', 'graph', 'config', 'running', 'recenter', 'modules', 'batch')
    } finally {
      this.restoring = false
    }
  }

  // ---- grammar / engine lifecycle ----

  loadGrammar(g: Grammar, label = `Load “${g.name}”`) {
    this.grammar = g
    this.engine = new Engine(g)
    this.activeRuleId = g.rules[0]?.id ?? null
    this.running = false
    this.lastHighlight = null
    // A direct load is a full replace, so it clears any tracked module set.
    // (Module-aware loads go through addGrammarModule / setGrammarModules.)
    this.modules.clear()
    this.batch = null
    this.emit('grammar', 'rules', 'selectRule', 'graph', 'config', 'running', 'recenter', 'modules', 'batch')
    this.commit(label)
  }

  // ---- additive multi-grammar (module) loading ----
  //
  // Grammars double as composable MODULES: the user can load several at once
  // and the engine rewrites with the union of their rules. These methods keep
  // the loaded set, recompose via mergeGrammars(), and swap the engine in,
  // without a hard reset of unrelated state. Loading a single module is the
  // one-element case and behaves exactly like loadGrammar().

  /** Keys of the currently-loaded modules, in load order (primary first). */
  get loadedModuleKeys(): string[] {
    return [...this.modules.keys()]
  }

  /**
   * A flat view of every tracked module in load order, for the sidebar. The
   * `primary` flag marks the FIRST ENABLED module , the one that supplies the
   * start axiom + base config in the composed grammar.
   */
  get loadedModules(): LoadedModule[] {
    const primaryKey = [...this.modules.values()].find((m) => m.enabled)?.key
    return [...this.modules.values()].map((m) => ({
      key: m.key,
      title: m.title,
      enabled: m.enabled,
      primary: m.key === primaryKey,
    }))
  }

  isModuleLoaded(key: string): boolean {
    return this.modules.has(key)
  }

  /** Whether a loaded module is currently enabled (composed into the grammar). */
  isModuleEnabled(key: string): boolean {
    return this.modules.get(key)?.enabled ?? false
  }

  /**
   * Recompose `grammar` from the ENABLED modules and reload the engine.
   *
   * `preserveGraph` keeps the live working graph across the swap , adding,
   * toggling or removing a module changes only the RULE SET, never resetting
   * the graph the composition is building. Only the FIRST module load (an empty
   * set becoming non-empty) initialises the graph to the primary's start.
   */
  private recomposeModules(label: string, preserveGraph = false) {
    // Only ENABLED modules contribute rules; the first enabled one is primary
    // (supplies start/config). Disabled modules stay tracked but excluded.
    const enabled = [...this.modules.values()].filter((m) => m.enabled)
    const merged = mergeGrammars(enabled)
    // mergeGrammars returns null when nothing is enabled (empty set). Fall back
    // to a blank grammar so the workbench has something coherent to show and
    // never crashes , the user can re-enable a module to bring rules back.
    const keep = preserveGraph ? this.engine?.graph : undefined
    this.grammar = merged ?? buildExample('blank')
    this.engine = new Engine(this.grammar)
    // Swap rules in over the existing graph instead of resetting to the new
    // start (which would wipe the graph the primary established).
    if (keep && keep.nodes.length) this.engine.reset(cloneGraph(keep))
    this.activeRuleId = this.grammar.rules[0]?.id ?? null
    this.running = false
    this.lastHighlight = null
    this.batch = null
    this.emit('grammar', 'rules', 'selectRule', 'graph', 'config', 'running', 'recenter', 'modules', 'batch')
    this.commit(label)
  }

  /**
   * If no modules are tracked yet but a grammar is already loaded (from an
   * example, a single import, the initial grammar, …), register that grammar as
   * the PRIMARY module so a subsequent additive load COMPOSES onto it instead of
   * replacing it. Returns true if it seeded a module. No-op once any module is
   * tracked, or when the active grammar has no rules (nothing worth keeping).
   */
  private seedActiveGrammarAsModule(): boolean {
    if (this.modules.size > 0) return false
    const cur = this.grammar
    if (!cur.rules.length) return false
    // Key off the grammar id so it stays stable and won't collide with the
    // file-/example-keyed modules added afterwards.
    this.modules.set(cur.id, { key: cur.id, title: cur.name, grammar: cur, enabled: true })
    return true
  }

  /**
   * Add a grammar module to the active set and recompose. If the key is already
   * loaded its grammar is refreshed in place (kept at its existing position, so
   * the primary/start choice is stable). `title` is used to namespace the
   * module's rule names/groups when several modules are composed.
   *
   * "Add" is strictly ADDITIVE: if a grammar is already loaded outside the
   * module system (an example/import via loadGrammar), it is first seeded as the
   * primary module so this call composes onto it rather than replacing it.
   */
  addGrammarModule(key: string, grammar: Grammar, title = grammar.name) {
    const prev = this.modules.get(key)
    // Fold any already-loaded standalone grammar into the module set first, so
    // adding the new module keeps it instead of recomposing it away.
    const seeded = this.seedActiveGrammarAsModule()
    const wasEmpty = this.modules.size === 0
    // Re-adding keeps the module at its position AND its enabled state; a fresh
    // add starts enabled.
    this.modules.set(key, { key, title, grammar, enabled: prev?.enabled ?? true })
    // The very first module load initialises the graph to its start; adding to
    // an existing composition (or one we just seeded) keeps the live graph , only
    // the rule set grows.
    this.recomposeModules(prev ? `Refresh “${title}”` : `Add “${title}”`, !wasEmpty || seeded)
  }

  /** Remove a module from the active set and recompose. No-op if not loaded. */
  removeGrammarModule(key: string) {
    const mod = this.modules.get(key)
    if (!mod) return
    this.modules.delete(key)
    this.recomposeModules(`Remove “${mod.title}”`, true)
  }

  /**
   * Enable or disable a tracked module without removing it. A disabled module
   * stays in the loaded set (and in the sidebar) but its rules are excluded
   * from the composed grammar until re-enabled , no re-import needed. No-op if
   * the key isn't loaded or the flag is already in the requested state.
   */
  setModuleEnabled(key: string, on: boolean) {
    const mod = this.modules.get(key)
    if (!mod || mod.enabled === on) return
    mod.enabled = on
    this.recomposeModules(`${on ? 'Enable' : 'Disable'} “${mod.title}”`, true)
  }

  /** Flip a tracked module's enabled flag. Returns the new state (true = on). */
  toggleModuleEnabled(key: string): boolean {
    const mod = this.modules.get(key)
    if (!mod) return false
    this.setModuleEnabled(key, !mod.enabled)
    return mod.enabled
  }

  /**
   * Toggle a module on/off. Returns the resulting loaded state (true = now
   * loaded). Convenience for checkbox-style UIs.
   */
  toggleGrammarModule(key: string, grammar: Grammar, title = grammar.name): boolean {
    if (this.modules.has(key)) {
      this.removeGrammarModule(key)
      return false
    }
    this.addGrammarModule(key, grammar, title)
    return true
  }

  /**
   * Reorder the tracked modules to match `orderedKeys` (a permutation of the
   * loaded keys) and recompose. Order matters: the first ENABLED module is
   * PRIMARY and supplies the start axiom + base config, and rules compose in
   * this order. Unknown keys are ignored; any loaded key missing from the list
   * keeps its existing relative position after the listed ones, so a partial
   * list never drops modules. The live working graph is preserved. No-op if the
   * order is unchanged or fewer than two modules are loaded.
   */
  reorderModules(orderedKeys: string[]) {
    if (this.modules.size < 2) return
    const next = new Map<string, TrackedModule>()
    for (const k of orderedKeys) {
      const m = this.modules.get(k)
      if (m && !next.has(k)) next.set(k, m)
    }
    for (const [k, m] of this.modules) if (!next.has(k)) next.set(k, m)
    const before = [...this.modules.keys()]
    const after = [...next.keys()]
    if (before.every((k, i) => k === after[i])) return
    this.modules = next
    this.recomposeModules('Reorder modules', true)
  }

  /** Replace the whole module set in one shot (e.g. from a multi-select). Each
   *  module starts enabled unless it already carries an `enabled` flag. */
  setGrammarModules(mods: Array<GrammarModule & { enabled?: boolean }>) {
    this.modules = new Map(mods.map((m) => [m.key, { ...m, enabled: m.enabled ?? true }]))
    this.recomposeModules('Set grammar modules')
  }

  // ---- batch runs (seed sweep) ----
  //
  // Run the grammar to completion once per seed and keep every result so the
  // user can flip through the outputs. Seeds are either a consecutive RANGE
  // (reproducible) or freshly RANDOM (broader exploration). Each run resets the
  // engine, runs to its fixpoint/cap, and the final graph is captured.

  /** Whether a batch result set is currently loaded. */
  get batchActive(): boolean {
    return this.batch !== null
  }

  /** Index of the batch run currently shown on the canvas (-1 if none). */
  get batchIndex(): number {
    return this.batch?.index ?? -1
  }

  /** Read-only summaries of the loaded batch runs, in seed order. */
  get batchRuns(): BatchRunSummary[] {
    return (this.batch?.runs ?? []).map((r) => ({
      seed: r.seed,
      steps: r.steps,
      nodes: r.graph.nodes.length,
      edges: r.graph.edges.length,
    }))
  }

  /**
   * Run the active grammar once per seed and keep the results. Returns the
   * number of runs completed. The first result is shown on the canvas; use
   * {@link showBatchRun} / {@link nextBatchRun} / {@link prevBatchRun} to flip
   * through the rest. This is a synchronous sweep , bound `Max steps` / `Max
   * nodes` so each run terminates promptly.
   */
  runBatch(opts: BatchRunOptions): number {
    const count = Math.max(1, Math.min(500, Math.floor(opts.count) || 1))
    const seeds: number[] = []
    if (opts.mode === 'range') {
      const start = Math.floor(opts.start ?? this.grammar.config.seed) || 0
      for (let i = 0; i < count; i++) seeds.push(start + i)
    } else {
      for (let i = 0; i < count; i++) seeds.push(Math.floor(Math.random() * 1_000_000_000))
    }
    const runs: BatchRun[] = []
    for (const seed of seeds) {
      this.grammar.config.seed = seed
      this.engine.reset()
      const steps = this.engine.run()
      runs.push({ seed, graph: cloneGraph(this.engine.graph), steps })
    }
    this.batch = { runs, index: 0 }
    this.applyBatchRun(0)
    this.emit('batch')
    return runs.length
  }

  /** Show batch run `i` (wraps around) on the canvas. No-op without a batch. */
  showBatchRun(i: number) {
    if (!this.batch) return
    const n = this.batch.runs.length
    this.applyBatchRun(((i % n) + n) % n)
    this.emit('batch')
  }

  nextBatchRun() {
    if (this.batch) this.showBatchRun(this.batch.index + 1)
  }

  prevBatchRun() {
    if (this.batch) this.showBatchRun(this.batch.index - 1)
  }

  /** Discard the batch results (returns the canvas to normal run state). */
  clearBatch() {
    if (!this.batch) return
    this.batch = null
    this.emit('batch')
  }

  /** Load a captured batch run's graph onto the canvas and mark it current. */
  private applyBatchRun(i: number) {
    const run = this.batch?.runs[i]
    if (!run) return
    this.batch!.index = i
    // Sync config.seed so a follow-up Step/Reset reproduces this run, and load
    // the captured final graph as the live host graph.
    this.grammar.config.seed = run.seed
    this.engine.reset(run.graph)
    this.running = false
    this.lastHighlight = null
    this.emit('graph', 'recenter', 'config', 'running')
  }

  /** Reset the engine's host graph to the grammar's start (axiom). */
  resetGraph() {
    this.engine.reset()
    this.lastHighlight = null
    this.emit('graph', 'recenter')
  }

  /** Replace the start/axiom graph and reset. */
  setStartGraph(g: Graph, label = 'Set start graph') {
    this.grammar.start = g
    this.engine.reset(cloneGraph(g))
    this.lastHighlight = null
    this.batch = null
    this.emit('graph', 'recenter', 'batch')
    this.commit(label)
  }

  /** Use the current host graph as the new axiom. */
  pinCurrentAsStart() {
    this.grammar.start = cloneGraph(this.engine.graph)
    this.emit('grammar')
    this.commit('Pin as start')
  }

  get activeRule(): Rule | null {
    return this.grammar.rules.find((r) => r.id === this.activeRuleId) ?? null
  }

  selectRule(id: string | null) {
    this.activeRuleId = id
    this.emit('selectRule')
  }

  addRule(r: Rule) {
    this.grammar.rules.push(r)
    this.activeRuleId = r.id
    this.emit('rules', 'selectRule', 'graph')
    this.commit('Add rule')
  }

  removeRule(id: string) {
    const i = this.grammar.rules.findIndex((r) => r.id === id)
    if (i < 0) return
    this.grammar.rules.splice(i, 1)
    if (this.activeRuleId === id) this.activeRuleId = this.grammar.rules[0]?.id ?? null
    this.emit('rules', 'selectRule', 'graph')
    this.commit('Delete rule')
  }

  moveRule(id: string, dir: -1 | 1) {
    const i = this.grammar.rules.findIndex((r) => r.id === id)
    const j = i + dir
    if (i < 0 || j < 0 || j >= this.grammar.rules.length) return
    const arr = this.grammar.rules
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
    this.emit('rules')
    this.commit('Reorder rules')
  }

  /**
   * Move rule `dragId` to just before `targetId` in the rule list (drag-reorder).
   * No-op if either id is unknown or they're the same rule.
   */
  reorderRules(dragId: string, targetId: string) {
    if (dragId === targetId) return
    const arr = this.grammar.rules
    const from = arr.findIndex((r) => r.id === dragId)
    if (from < 0) return
    const [moved] = arr.splice(from, 1)
    const to = arr.findIndex((r) => r.id === targetId)
    if (to < 0) {
      arr.splice(from, 0, moved)
      return
    } // target vanished , restore
    arr.splice(to, 0, moved)
    this.emit('rules')
    this.commit('Reorder rules')
  }

  /**
   * Reorder rules so their GROUPS follow `orderedGroups` (each entry is a rule
   * `group` string, or '' / null for the ungrouped bucket). Rules keep their
   * order WITHIN a group; any group not named is left in its existing relative
   * position after the named ones, so a partial list never drops rules. Backs
   * the Rules panel's drag-to-reorder of group sections. No-op if nothing moves.
   */
  reorderRuleGroups(orderedGroups: Array<string | null>) {
    const keyOf = (r: Rule) => r.group || ''
    // Bucket rules by group, preserving first-seen + intra-group order.
    const buckets = new Map<string, Rule[]>()
    const seen: string[] = []
    for (const r of this.grammar.rules) {
      const k = keyOf(r)
      if (!buckets.has(k)) {
        buckets.set(k, [])
        seen.push(k)
      }
      buckets.get(k)!.push(r)
    }
    // Named groups first (deduped, existing only), then the rest in old order.
    const want: string[] = []
    for (const g of orderedGroups) {
      const k = g || ''
      if (buckets.has(k) && !want.includes(k)) want.push(k)
    }
    for (const k of seen) if (!want.includes(k)) want.push(k)
    if (want.length === seen.length && want.every((k, i) => k === seen[i])) return
    this.grammar.rules = want.flatMap((k) => buckets.get(k)!)
    this.emit('rules')
    this.commit('Reorder rule groups')
  }

  /** Called after editing a rule's internals so engine re-reads it. */
  touchRules(label = 'Edit rule') {
    this.emit('rules', 'graph')
    this.commit(label)
  }

  step() {
    const r = this.engine.step()
    if (r.applied) {
      this.lastHighlight = {
        created: new Set([...r.createdNodes, ...r.createdEdges]),
        deleted: new Set([...r.deletedNodes, ...r.deletedEdges]),
      }
    }
    this.emit('graph')
    return r
  }
}
